/*
 * Copyright 2022-2026 NXGN Management, LLC. All Rights Reserved.
 */

import { Injectable } from "@angular/core";
import { HttpClient, HttpErrorResponse, HttpHeaders, HttpParams } from "@angular/common/http";
import { Observable, Subject, throwError } from "rxjs";
import { catchError, map } from "rxjs/operators";

import { MERCHANT_CONSTANTS, REST_CONSTANTS } from "../utils/data.rest.constants";
import {
  ApiCustomer,
  CreditCardEnum,
  Merchant,
  MerchantStatusAndDeactivatedDate,
  Chargeback,
  BeneficialOwner,
  AccountDetails,
  InstaMedAccountDetails,
  GlobalPaymentsAccountDetails,
  ContractedRates,
  FraudVariables,
  QBPayAccountDetails,
  WorldpayUnderwritingStatus,
  MerchantMigrationDetails,
  MerchantSearchSummary
} from "../models";
import { PracticeUserRoles, UserRoles } from "../practice-user";
import { DatePipe } from "@angular/common";
import {
  EffectiveDateWithAssociatedMerchants,
  MigrationConfirmation,
  AchEcheckAssociatedMerchant,
  AchEcheckBulkUpdateRequest,
  AchEcheckBulkUpdateResponse,
  MerchantSummaryByClient,
  MerchantPracticeRef
} from "../models/TokenModel";
import { client } from "../models/client";
import { Location } from "../models/merchant";
import { AuthService } from "./auth.service";

@Injectable({
  providedIn: "root"
})
export class MerchantService {
  public displayMigrationComponents = false;

  merchantChange: Subject<Merchant> = new Subject<Merchant>();
  private NG_PAY_CUSTOMER = "NG Pay";
  private _merchant!: Merchant;
  private _apiCustomers!: ApiCustomer[];

  merchantMigrationDetails: MerchantMigrationDetails = {
    effectiveDate: "",
    instaMedMmid: 0,
    migratedMerchantName: "",
    migrationStatus: "",
    previousMerchantName: "",
    previousMmid: 0,
    previousProcessor: "",
    toBeMigrated: null,
    practiceId: 0
  };

  constructor(private http: HttpClient, private datePipe: DatePipe, private authService: AuthService) {}

  /**
   * Execute merchant search for given search query
   *  - it is parsed and processed on the REST service side
   *  - this just passes data around
   *
   * @param {string} searchString
   *
   * @returns {*}
   */
  searchMerchants(searchString: string): Observable<MerchantSearchSummary[]> {
    searchString = searchString.trim();

    const params = searchString ? new HttpParams().set("searchFor", searchString) : {};
    const headers = REST_CONSTANTS.JSON_HEADER;
    const options = { headers: headers, params: params };

    const url = `${REST_CONSTANTS.MERCHANT_BASE_URL_V9}/merchants/search`;

    return this.http.get<MerchantSearchSummary[]>(url, options).pipe(catchError(MerchantService.handleError));
  }

  downloadFullMerchantList(): Observable<any> {
    const url = `${REST_CONSTANTS.MERCHANT_BASE_URL_V9}/merchants/report`;
    const apiHeaders = new HttpHeaders({ "Content-Type": "application/vnd.ms-excel" });
    return this.http
      .get<any>(url, {
        headers: apiHeaders,
        responseType: "blob" as "json",
        observe: "response" as "body"
      })
      .pipe(catchError(MerchantService.handleError));
  }

  createMerchants(merchant: Merchant) {
    const url = `${REST_CONSTANTS.MERCHANT_BASE_URL_INTERNAL}/merchants`;
    return this.http
      .post<Merchant>(url, merchant, { headers: REST_CONSTANTS.JSON_HEADER })
      .pipe(catchError(MerchantService.handleError));
  }

  cancelPreviousMmidLinkage(previousMmid: number) {
    const url = `${REST_CONSTANTS.MERCHANT_BASE_URL_INTERNAL}/merchants/${previousMmid}/merchant-migration`;
    return this.http.delete(url, { headers: REST_CONSTANTS.JSON_HEADER }).pipe(catchError(MerchantService.handleError));
  }

  searchPracticeMerchant(practiceId: number) {
    const url = `${REST_CONSTANTS.MERCHANT_BASE_URL_INTERNAL}/merchantUser/practice/${practiceId}`;
    return this.http
      .get<PracticeUserRoles[]>(url, { headers: REST_CONSTANTS.JSON_HEADER })
      .pipe(catchError(MerchantService.handleError));
  }

  fetchClientMerchant(practiceName: string) {
    const url = `${REST_CONSTANTS.MERCHANT_BASE_URL_INTERNAL}/merchants/client?practiceName=${practiceName}`;
    return this.http
      .get<client[]>(url, { headers: REST_CONSTANTS.JSON_HEADER })
      .pipe(catchError(MerchantService.handleError));
  }

  fetchClientLocations(siteId: string, enterpriseId: string, practiceId: string) {
    const url = `${REST_CONSTANTS.MERCHANT_BASE_URL_INTERNAL}/merchants/sites/${siteId}/enterprises/${enterpriseId}/practices/${practiceId}/locations`;
    return this.http
      .get<Location[]>(url, { headers: REST_CONSTANTS.JSON_HEADER })
      .pipe(catchError(MerchantService.handleError));
  }

  handlePracticeUserRoles(practiceId: number, data: UserRoles, practiceStaffId: number) {
    const url = `${REST_CONSTANTS.MERCHANT_BASE_URL_INTERNAL}/merchantUser/practice/${practiceId}/practiceStaffId/${practiceStaffId}`;
    return this.http
      .post<PracticeUserRoles>(url, data, { headers: REST_CONSTANTS.JSON_HEADER })
      .pipe(catchError(MerchantService.handleError));
  }

  getMerchantDetails(merchantId: number): Observable<Merchant> {
    const url = `${REST_CONSTANTS.MERCHANT_BASE_URL_INTERNAL}/merchants/${merchantId}`;
    return this.http.get<Merchant>(url, { headers: REST_CONSTANTS.JSON_HEADER }).pipe(
      map((merchant: Merchant) => {
        if (!merchant.payAPICustomer) {
          merchant.payAPICustomer = this.getNgPayCustomer();
        }
        this.merchant = merchant;
        return merchant;
      }),
      catchError(MerchantService.handleError)
    );
  }

  getPreviousMerchantDetails(merchantId: number): Observable<MerchantMigrationDetails> {
    const url = `${REST_CONSTANTS.MERCHANT_BASE_URL_INTERNAL}/merchants/${merchantId}/migration-details`;
    return this.http.get<MerchantMigrationDetails>(url, { headers: REST_CONSTANTS.JSON_HEADER }).pipe(
      map((merchantMigrationInfo: MerchantMigrationDetails) => {
        return merchantMigrationInfo;
      }),
      catchError(MerchantService.handleError)
    );
  }

  generateMigrationInfoVerbiage(migrationDetails: MerchantMigrationDetails): string {
    let migrationInfo = `${migrationDetails.previousMerchantName} has been ${
      migrationDetails.migrationStatus === "SCHEDULED" || migrationDetails.migrationStatus === "NOT_MIGRATED"
        ? "scheduled for migration"
        : "migrated"
    }.`;
    if (migrationDetails.effectiveDate) {
      migrationInfo = migrationInfo
        .slice(0, migrationInfo.length - 1)
        .concat(
          ` as  ${migrationDetails.instaMedMmid} with an effective date( ${migrationDetails.effectiveDate}) as the date for the migration.`
        );
    }
    return migrationInfo;
  }

  updateMerchantStatus(merchantId: number, status: string): Observable<MerchantStatusAndDeactivatedDate> {
    const url = `${REST_CONSTANTS.MERCHANT_BASE_URL_INTERNAL}/merchants/${merchantId}/status`;
    return this.http
      .put<MerchantStatusAndDeactivatedDate>(url, { status: status }, { headers: REST_CONSTANTS.JSON_HEADER })
      .pipe(catchError(MerchantService.handleError));
  }

  worldPayHealthCheck(): Observable<any> {
    const url = `${REST_CONSTANTS.MERCHANT_BASE_URL_INTERNAL}/merchants/wphealthcheck`;
    return this.http
      .get<any>(url, { headers: REST_CONSTANTS.JSON_HEADER })
      .pipe(catchError(MerchantService.handleError));
  }

  getWorldPayUnderwritingStatus(merchantId: number): Observable<WorldpayUnderwritingStatus> {
    const url = `${REST_CONSTANTS.MERCHANT_BASE_URL_INTERNAL}/merchants/${merchantId}/status`;
    return this.http
      .get<WorldpayUnderwritingStatus>(url, { headers: REST_CONSTANTS.JSON_HEADER })
      .pipe(catchError(MerchantService.handleError));
  }

  createBeneficialOwner(merchantId: number, owner: BeneficialOwner): Observable<BeneficialOwner> {
    const url = `${REST_CONSTANTS.MERCHANT_BASE_URL_INTERNAL}/merchants/${merchantId}/wpSubMerchant/owner`;
    return this.http
      .post<BeneficialOwner>(url, owner, { headers: REST_CONSTANTS.JSON_HEADER })
      .pipe(catchError(MerchantService.handleError));
  }

  updateBeneficialOwner(
    merchantId: number,
    beneficialOwnerId: number,
    owner: BeneficialOwner
  ): Observable<BeneficialOwner> {
    const url = `${REST_CONSTANTS.MERCHANT_BASE_URL_INTERNAL}/merchants/${merchantId}/wpSubMerchant/owner/${beneficialOwnerId}`;
    return this.http
      .put<BeneficialOwner>(url, owner, { headers: REST_CONSTANTS.JSON_HEADER })
      .pipe(catchError(MerchantService.handleError));
  }

  deleteBeneficialOwner(merchantId: number, beneficialOwnerId: number): Observable<BeneficialOwner> {
    const url = `${REST_CONSTANTS.MERCHANT_BASE_URL_INTERNAL}/merchants/${merchantId}/wpSubMerchant/owner/${beneficialOwnerId}`;
    return this.http
      .delete<BeneficialOwner>(url, { headers: REST_CONSTANTS.JSON_HEADER })
      .pipe(catchError(MerchantService.handleError));
  }

  getChargeBacks(merchantId: number): Observable<Chargeback[]> {
    const url = `${REST_CONSTANTS.MERCHANT_GATEWAY_PROXY_URL}/merchant/${merchantId}/chargebacks`;
    return this.http
      .get<Chargeback[]>(url, { headers: REST_CONSTANTS.JSON_HEADER })
      .pipe(catchError(MerchantService.handleError));
  }

  updateMerchantGeneralInfo(merchant: Merchant): Observable<Merchant> {
    const url = `${REST_CONSTANTS.MERCHANT_BASE_URL_INTERNAL}/merchants/${merchant.id}/generalinfo`;
    return this.http
      .put<Merchant>(url, merchant, { headers: REST_CONSTANTS.JSON_HEADER })
      .pipe(catchError(MerchantService.handleError));
  }

  updateInstamedAccountDetails(
    merchantId: number,
    accountDetails: InstaMedAccountDetails
  ): Observable<InstaMedAccountDetails> {
    const url = `${REST_CONSTANTS.MERCHANT_BASE_URL_INTERNAL}/merchants/${merchantId}/accountDetails/instamed`;
    return this.http
      .put<InstaMedAccountDetails>(url, accountDetails, { headers: REST_CONSTANTS.JSON_HEADER })
      .pipe(catchError(MerchantService.handleError));
  }

  updateGlobalPaymentsAccountDetails(
    merchantId: number,
    accountDetails: GlobalPaymentsAccountDetails
  ): Observable<GlobalPaymentsAccountDetails> {
    const url = `${REST_CONSTANTS.MERCHANT_BASE_URL_INTERNAL}/merchants/${merchantId}/accountDetails/globalpayments`;
    return this.http
      .put<GlobalPaymentsAccountDetails>(url, accountDetails, { headers: REST_CONSTANTS.JSON_HEADER })
      .pipe(catchError(MerchantService.handleError));
  }

  updateAccountDetails(merchantId: number, accountDetails: AccountDetails): Observable<AccountDetails> {
    const url = `${REST_CONSTANTS.MERCHANT_BASE_URL_INTERNAL}/merchants/${merchantId}/accountDetails`;
    return this.http
      .put<AccountDetails>(url, accountDetails, { headers: REST_CONSTANTS.JSON_HEADER })
      .pipe(catchError(MerchantService.handleError));
  }

  updateElementMerchant(updateType: string, merchant: Merchant): Observable<Merchant> {
    const url = `${REST_CONSTANTS.MERCHANT_BASE_URL_INTERNAL}/merchants/${merchant.id}/wpSubMerchant`;
    const params = updateType ? new HttpParams().set("updateType", updateType) : {};
    const headers = REST_CONSTANTS.JSON_HEADER;
    const options = { headers: headers, params: params };
    return this.http.put<Merchant>(url, merchant, options).pipe(
      map((merchant: Merchant) => {
        this.merchant = merchant;
        return this.merchant;
      }),
      catchError(MerchantService.handleError)
    );
  }

  updateCreditCards(creditCards: CreditCardEnum[], merchant: Merchant) {
    const url = `${REST_CONSTANTS.MERCHANT_BASE_URL_INTERNAL}/merchants/${merchant.id}/creditcards`;

    return this.http
      .put<CreditCardEnum[]>(url, creditCards, { headers: REST_CONSTANTS.JSON_HEADER })
      .pipe(catchError(MerchantService.handleError));
  }

  updateFraudsData(merchant: Merchant, fraudRisk: FraudVariables): Observable<FraudVariables> {
    const url = `${REST_CONSTANTS.MERCHANT_BASE_URL_INTERNAL}/merchants/${merchant.id}/frauds`;

    return this.http
      .put<FraudVariables>(url, fraudRisk, { headers: REST_CONSTANTS.JSON_HEADER })
      .pipe(catchError(MerchantService.handleError));
  }

  getPayApiCustomers(): Observable<ApiCustomer[]> {
    const url = `${REST_CONSTANTS.MERCHANT_BASE_URL_INTERNAL}/apiCustomer`;
    return this.http.get<ApiCustomer[]>(url, { headers: REST_CONSTANTS.JSON_HEADER }).pipe(
      map((customers) => {
        this.apiCustomers = customers;
        return this.apiCustomers;
      }),
      catchError(MerchantService.handleError)
    );
  }

  setContractedRates(merchantId: number, contractedRates: ContractedRates): Observable<ContractedRates> {
    const url = `${REST_CONSTANTS.MERCHANT_BASE_URL_INTERNAL}/merchants/${merchantId}/rates`;
    return this.http
      .put<ContractedRates>(url, contractedRates, { headers: REST_CONSTANTS.JSON_HEADER })
      .pipe(catchError(MerchantService.handleError));
  }

  dateToMMddyyyy(date: Date): string {
    return <string>this.datePipe.transform(date, "MM/dd/yyyy");
  }

  isWorldPay(merchant: Merchant) {
    return merchant.accountDetails.preferredProcessor?.toLowerCase() === MERCHANT_CONSTANTS.WORLDPAY.toLowerCase();
  }

  isInstaMed(merchant: Merchant): boolean {
    return merchant.accountDetails.preferredProcessor?.toLowerCase() === MERCHANT_CONSTANTS.INSTAMED.toLowerCase();
  }

  isGlobalPayments(merchant: Merchant): boolean {
    return (
      merchant.accountDetails.preferredProcessor?.toLowerCase() === MERCHANT_CONSTANTS.GLOBALPAYMENTS.toLowerCase()
    );
  }

  isPracticeIdMatched(practiceId: number): boolean {
    return this.merchantMigrationDetails.practiceId === practiceId;
  }

  collectChargeBack(merchantId: number, chargeBack: Chargeback): Observable<Chargeback> {
    const url = `${REST_CONSTANTS.MERCHANT_GATEWAY_PROXY_URL}/merchant/${merchantId}/chargebacks`;

    return this.http
      .post<Chargeback>(url, chargeBack, { headers: REST_CONSTANTS.JSON_HEADER })
      .pipe(catchError(MerchantService.handleError));
  }

  updateQBPayAccountDetails(merchantId: number, accountDetails: QBPayAccountDetails): Observable<void> {
    const url = `${REST_CONSTANTS.MERCHANT_BASE_URL_V9}/merchants/${merchantId}/qbpayAccountDetails`;
    return this.http
      .put<void>(url, accountDetails, { headers: REST_CONSTANTS.JSON_HEADER })
      .pipe(catchError(MerchantService.handleError));
  }

  getMerchantsAssociatedWithPractice(practiceId: number): Observable<MigrationConfirmation> {
    const url = `${REST_CONSTANTS.MERCHANT_BASE_URL_INTERNAL}/merchants/practices/${practiceId}/merchants`;
    return this.http
      .get<MigrationConfirmation>(url, { headers: REST_CONSTANTS.JSON_HEADER })
      .pipe(catchError(MerchantService.handleError));
  }

  getAssociatedMmidsForPractice(practiceId: number): Observable<AchEcheckAssociatedMerchant[]> {
    const url = `${REST_CONSTANTS.MERCHANT_BASE_URL_INTERNAL}/merchants/practices/${practiceId}/associated-mmids`;
    return this.http
      .get<AchEcheckAssociatedMerchant[]>(url, { headers: REST_CONSTANTS.JSON_HEADER })
      .pipe(catchError(MerchantService.handleError));
  }

  bulkUpdateAchEcheck(
    practiceId: number,
    request: AchEcheckBulkUpdateRequest
  ): Observable<AchEcheckBulkUpdateResponse> {
    const url = `${REST_CONSTANTS.MERCHANT_BASE_URL_INTERNAL}/merchants/practices/${practiceId}/ach-echeck`;
    return this.http
      .put<AchEcheckBulkUpdateResponse>(url, request, { headers: REST_CONSTANTS.JSON_HEADER })
      .pipe(catchError(MerchantService.handleError));
  }

  getMerchantsByClientDetails(
    siteId: string,
    enterpriseId: string,
    practiceId: string
  ): Observable<MerchantSummaryByClient[]> {
    const url = `${REST_CONSTANTS.MERCHANT_BASE_URL_INTERNAL}/merchants/sites/${siteId}/enterprises/${enterpriseId}/practices/${practiceId}`;
    return this.http
      .get<MerchantSummaryByClient[]>(url, { headers: REST_CONSTANTS.JSON_HEADER })
      .pipe(catchError(MerchantService.handleError));
  }

  getMerchantPmPractice(merchantId: number): Observable<MerchantPracticeRef> {
    const url = `${REST_CONSTANTS.MERCHANT_BASE_URL_INTERNAL}/merchants/${merchantId}/pm-practice`;
    return this.http
      .get<MerchantPracticeRef>(url, { headers: REST_CONSTANTS.JSON_HEADER })
      .pipe(catchError(MerchantService.handleError));
  }

  updateEffectiveDateToAssociatedMerchants(
    merchantsData: EffectiveDateWithAssociatedMerchants
  ): Observable<MigrationConfirmation> {
    const url = `${REST_CONSTANTS.MERCHANT_BASE_URL_INTERNAL}/merchants/migration-date`;
    return this.http
      .patch<MigrationConfirmation>(url, merchantsData, { headers: REST_CONSTANTS.JSON_HEADER })
      .pipe(catchError(MerchantService.handleError));
  }

  set merchant(merchant: Merchant) {
    this._merchant = merchant;
    this.merchantChange.next(this._merchant);
  }

  get merchant() {
    return this._merchant;
  }

  get apiCustomers() {
    return this._apiCustomers;
  }

  set apiCustomers(customerList: ApiCustomer[]) {
    this._apiCustomers = customerList;
    this._apiCustomers.push(this.getNgPayCustomer());
  }

  private static handleError(error: HttpErrorResponse) {
    return throwError(error);
  }

  private getNgPayCustomer(): ApiCustomer {
    return { id: 0, customerName: this.NG_PAY_CUSTOMER };
  }
}
